<template>
    <div class="product-select-comp" ref="dropdownRef">
        <div class="flex items-center relative search-input-container">
            <input type="search" name="product-search" autocomplete="off" :placeholder="placeholder" id="order-template-search-product-input"
                :class="accessType === 'orderTemplatesearchBar' ? 'h-[32px] py-0 rounded-md' : accessType === 'createEstimate' ? 'h-12 rounded' : 'min-h-[40px] py-2 rounded-md'"
                class="search-product-input !border-1.5 placeholder:font-normal !border-[#D1D1D1] text-base px-3 w-full focus:outline-none bg-[#fff]"
                v-model="search" @focus="onFocusInput" @click="isOpen = true" @input="onInputChange($event)"
                @keydown.down.prevent="onArrowDown" @keydown.up.prevent="onArrowUp" @keydown.enter="onEnter"
                @keydown.esc="onEscape" />
            <i v-if="!canClearSearch"
                class="ri-search-line text-[18px] absolute right-1.5 top-1/2 -translate-y-1/2 text-[#000000]"></i>

            <div v-if="canClearSearch" class="relative z-50">
                <button type="button" @click="onClear" id="order-wizard-search-product-clear-button"
                    class="absolute text-[18px] right-1.5 top-1/2 -translate-y-1/2 bg-[#ffff] text-[#000000]">
                    <i class="ri-close-line text-xl text-[#000000]"></i>
                </button>
            </div>

        </div>


        <div v-if="isOpen"
            class="absolute z-10 mt-1 bg-white border border-[#D1D1D1] rounded-md shadow-lg overflow-hidden"
            :class="isGlobal ? 'w-[330px] md:!w-[623px] max-h-[450px]' : 'w-[330px] md:!w-[442px] h-[402px]'">
            <!-- Scrollable Content -->
            <div class="overflow-y-auto overflow-x-hidden"
                :class="isGlobal ? 'max-h-[400px]' : 'max-h-[350px] min-h-[350px]'">
                <div v-if="isLoading" class="flex items-center justify-center h-[300px]">
                    <img class="h-10 w-10 ml-3" :src="`${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_BASE_PATH}Loader.gif`" />
                </div>


                <div v-else>
                    <ItemSearchSection v-for="item in productResults" :keyType="item.category.id"
                        :category="item.category" :items="item.items" :product-count="count[item.category.id]"
                        :active-product="activeProduct" :selected-product="selectedProductItem"
                        :full-width="isGlobal"
                        @selected-pro="selectedProduct($event)" />

                    <!-- No results: explanatory message above the Browse Full Catalog button -->
                    <div v-if="showBrowseCatalogCta && !hasAnyResults" class="px-3 py-4"
                        id="global-quick-search-no-results">
                        <p class="m-0 text-sm text-[#464646] text-left">{{ emptyMessage }}</p>
                    </div>
                </div>
                <!-- FIX Issue 2 polish: Only show yellow panel when there are actual results -->
                <div class="w-full md:w-[440px] px-3 py-3" v-if="!isGlobal && !isLoading && productResults.length > 0">
                    <div class="w-full bg-[#FDF9C8] border border-[#FAD58C] rounded-md px-2 py-2">
                        <div class="flex items-start gap-1 text-sm text-[#464646]">
                            <i class="ri-information-line text-2xl leading-none mt-[2px] text-[#69470C]"
                                aria-hidden="true"></i>
                            <p class="m-0 text-left">
                                You've reached the end of Simple Search. For the full catalog list, select
                                <b>'Browse Full Catalog'</b> button.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sticky Bottom Slider -->

            <div class="sticky bottom-0 pb-3 bg-[#fff] z-20 w-[310px] md:w-[440px]" v-if="!isGlobal">
                <OrderCatalogSlider accessPage="dropDown" :categoryName="categoryName" :currentTierId="currentTierId" />
            </div>

            <!-- Global search: always-visible Browse Full Catalog button -->
            <div v-if="showBrowseCatalogCta" class="sticky bottom-0 pb-3 pt-1 bg-[#fff] z-20">
                <div class="flex items-center justify-center px-3">
                    <button type="button" id="global-quick-search-browse-catalog" @click="onBrowseCatalog"
                        class="bg-[#fff] h-[40px] border-1.5 border-[#B9B9B9] w-full">
                        <i class="ri-book-read-line text-lg text-[#262626] pr-1"></i>
                        <span class="text-[#171717] text-base font-normal">{{ browseCatalogLabel }}</span>
                    </button>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import ItemSearchSection from '../OrderTemplate/ItemSearchSection.vue';
import OrderCatalogSlider from '../OrderCatalog/OrderCatalogSlider.vue';
import { useI18n } from "vue-i18n";
import { getLanguage } from '@/utilities/cookies';
import { useGlobalCatalogStore } from "@/stores/globalCatalogStore";
// Initialize i18n
const { t, locale } = useI18n();
locale.value = getLanguage();
const globalCatalogStore = useGlobalCatalogStore();
import {
    MEDIA_ASSET_BASE_URL,
    MEDIA_ASSET_BASE_PATH,
} from '@/config/constants';

const props = defineProps({
    accessType: {
        type: String
    },
    productResults: {
        type: Array,
        default: []
    },
    count: {
        type: Object,
        default: () => ({})
    },
    idx: {
        type: Number,
        default: 0
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    categoryName: {
        type: String,
        default: null
    },
    currentTierId: {
        type: [Number, String],
        default: null
    },
    /** Input placeholder text. */
    placeholder: {
        type: String,
        default: 'Enter Item Description'
    },
    /** Enables Up/Down/Enter keyboard navigation over the results. */
    enableKeyboardNav: {
        type: Boolean,
        default: false
    },
    /** Shows the empty-state message and Browse Full Catalog CTA when there are no results. */
    showBrowseCatalogCta: {
        type: Boolean,
        default: false
    },
    emptyMessage: {
        type: String,
        default: ''
    },
    browseCatalogLabel: {
        type: String,
        default: ''
    },
    /** Keeps the search text after a selection instead of clearing it. */
    retainSearchOnSelect: {
        type: Boolean,
        default: false
    }
});
const emit = defineEmits([
    'selected-product',
    'on-search-text',
    'click-outside',
    'on-focus-search',
    'browse-catalog',
    'submit-search'
]);

/** Global search bar mode: wider dropdown, no catalog slider, empty-state CTA. */
const isGlobal = computed(() => props.accessType === 'globalSearchBar');

/** Flat, ordered result list used for keyboard navigation. */
const flatResults = computed(() =>
    Object.values(props.productResults || {}).reduce(
        (all, group) => [...all, ...((group && group.items) || [])],
        []
    )
);

const hasAnyResults = computed(() => flatResults.value.length > 0);

const activeIndex = ref(-1);
const activeProduct = computed(() =>
    activeIndex.value >= 0 ? flatResults.value[activeIndex.value] || null : null
);
const selectedProductItem = ref(null);

function moveActive(step) {
    if (!props.enableKeyboardNav || !hasAnyResults.value) return;
    isOpen.value = true;
    const total = flatResults.value.length;
    activeIndex.value = (activeIndex.value + step + total) % total;
}

function onArrowDown() {
    moveActive(1);
}

function onArrowUp() {
    moveActive(-1);
}

function onEnter() {
    if (props.enableKeyboardNav && activeProduct.value) {
        selectedProduct({ product: activeProduct.value, type: 'keyboard' });
        return;
    }
    emit('submit-search', search.value);
}

function onEscape() {
    closeDropdown();
}

function closeDropdown() {
    isOpen.value = false;
    activeIndex.value = -1;
}

function onBrowseCatalog() {
    emit('browse-catalog');
    closeDropdown();
}

const dropdownRef = ref(null);

function onInputChange() {
    activeIndex.value = -1;
    selectedProductItem.value = null;
    emit('on-search-text', search.value);
};

function onFocusInput() {
    isOpen.value = true;
    emit('on-focus-search');
}

const canClearSearch = computed(() => {
    return search.value && search.value.length >= 0;
});

function onClear() {
    search.value = '';
    activeIndex.value = -1;
    selectedProductItem.value = null;
    emit('on-search-text', search.value);
};

onMounted(() => {
    // Use click instead of mousedown to prevent race condition with product selection
    // mousedown fires before click, causing dropdown to close before product selection completes
    document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
});

function handleClickOutside(e) {
    if (globalCatalogStore.isOpenCatalogSlider) {
        return;
    }
    if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
        closeDropdown();
        selectedProductItem.value = null;
        if (!props.retainSearchOnSelect) {
            search.value = '';
        }
        emit('click-outside');
    }
}

const selectedProduct = ({ product, type }) => {
    // Global search: first click highlights the row, a second click navigates.
    if (isGlobal.value && type !== 'keyboard') {
        const currentId = selectedProductItem.value?.productId ?? selectedProductItem.value?.ProductId;
        const nextId = product?.productId ?? product?.ProductId;
        if (currentId !== nextId) {
            selectedProductItem.value = product;
            return;
        }
    }

    emit('selected-product', { product, type, index: props.idx });
    closeDropdown();
    selectedProductItem.value = null;
    if (!props.retainSearchOnSelect) {
        search.value = '';
    }
}

const isOpen = ref(false);
const search = ref('');
</script>